import React, { useState, useEffect } from 'react';
import { Cookie, Settings, Info, Shield } from 'lucide-react';
import './Cookie.css';

interface CookieConsentProps {
  onAccept?: (preferences: CookiePreferences) => void;
  onRefuse?: () => void;
  companyName?: string;
  privacyPolicyUrl?: string;
}

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const GA_MEASUREMENT_ID = "G-VZ4TNWLKEE"; // <-- Remplace par ton ID Google Analytics

const loadGoogleAnalytics = () => {
  if (document.getElementById('ga-script')) return; // évite double chargement

  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script1.id = 'ga-script';

  const script2 = document.createElement('script');
  script2.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${GA_MEASUREMENT_ID}', { anonymize_ip: true });
  `;

  document.head.appendChild(script1);
  document.head.appendChild(script2);
};

const CookieConsent: React.FC<CookieConsentProps> = ({
                                                       onAccept,
                                                       onRefuse,
                                                       companyName = "Votre Hébergeur Gaming",
                                                       privacyPolicyUrl = "#"
                                                     }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: true,
    marketing: true,
    functional: true
  });

  useEffect(() => {
    const consentData = localStorage.getItem('cookieConsent');
    if (consentData) {
      try {
        const parsed = JSON.parse(consentData);
        setPreferences(parsed.preferences);

        // Si analytics accepté, charger Google Analytics
        if (parsed.preferences.analytics) {
          loadGoogleAnalytics();
        }
      } catch (error) {
        console.error('Erreur lecture cookies:', error);
        setIsVisible(true);
      }
    } else {
      setIsVisible(true);
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      preferences: prefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }));
  };

  const handleAcceptAll = () => {
    setIsLoading(true);
    const fullConsent: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };

    saveConsent(fullConsent);
    setPreferences(fullConsent);
    loadGoogleAnalytics(); // Charge GA dès acceptation
    if (onAccept) onAccept(fullConsent);
    setIsVisible(false);
    setIsLoading(false);
  };

  const handleRefuseAll = () => {
    const minimalConsent: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    saveConsent(minimalConsent);
    setPreferences(minimalConsent);
    if (onRefuse) onRefuse();
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    setIsLoading(true);
    saveConsent(preferences);
    if (preferences.analytics) {
      loadGoogleAnalytics(); // Charge GA si analytics activé
    }
    if (onAccept) onAccept(preferences);
    setIsVisible(false);
    setIsLoading(false);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const cookieCategories = [
    {
      key: 'necessary' as keyof CookiePreferences,
      title: 'Cookies Nécessaires',
      description: 'Ces cookies sont essentiels au fonctionnement du site.',
      required: true,
      examples: 'Authentification, sécurité'
    },
    {
      key: 'functional' as keyof CookiePreferences,
      title: 'Cookies Fonctionnels',
      description: 'Ces cookies améliorent les fonctionnalités du site.',
      required: false,
      examples: 'Préférences utilisateur'
    },
    {
      key: 'analytics' as keyof CookiePreferences,
      title: 'Cookies Analytiques',
      description: 'Ces cookies nous aident à comprendre l\'utilisation du site.',
      required: false,
      examples: 'Google Analytics'
    },
    {
      key: 'marketing' as keyof CookiePreferences,
      title: 'Cookies Marketing',
      description: 'Ces cookies sont utilisés pour la publicité ciblée.',
      required: false,
      examples: 'Publicités personnalisées'
    }
  ];

  if (!isVisible) {
    return (
        <button
            onClick={() => setIsVisible(true)}
            className="cookie-manager-button"
            title="Gérer les cookies"
        >
          <Cookie size={20} />
          Gérer les cookies
        </button>
    );
  }

  return (
      <div className="cookie-popup-overlay">
        <div className="cookie-popup">
          <div className="cookie-content">
            <div className="cookie-header">
              <div className="cookie-header-content">
                <Cookie className="cookie-icon" size={24} />
                <h3 className="cookie-title">Gestion des Cookies</h3>
              </div>
              <Shield className="shield-icon" size={24} />
            </div>

            {!showSettings ? (
                <>
                  <div className="cookie-description">
                    <Info size={20} />
                    <p>
                      Nous utilisons des cookies pour améliorer votre expérience.
                      Vous pouvez personnaliser vos choix ou accepter l'ensemble.
                    </p>
                  </div>

                  <div className="cookie-buttons">
                    <button
                        onClick={handleAcceptAll}
                        className="cookie-btn cookie-btn-accept"
                        disabled={isLoading}
                    >
                      {isLoading ? (
                          <>
                            <span className="loading-spinner" />
                            Acceptation...
                          </>
                      ) : (
                          'Tout accepter'
                      )}
                    </button>
                    <button
                        onClick={handleRefuseAll}
                        className="cookie-btn cookie-btn-refuse"
                        disabled={isLoading}
                    >
                      Tout refuser
                    </button>
                    <button
                        onClick={() => {
                          setPreferences({
                            necessary: true,
                            analytics: true,
                            marketing: true,
                            functional: true
                          });
                          setShowSettings(true);
                        }}
                        className="cookie-btn-settings"
                        disabled={isLoading}
                    >
                      <Settings size={16} />
                      Personnaliser
                    </button>
                  </div>
                </>
            ) : (
                <>
                  <div className="cookie-settings">
                    {cookieCategories.map(category => (
                        <div key={category.key} className="cookie-category">
                          <div className="cookie-category-header">
                            <div>
                              <h4>{category.title}</h4>
                              {category.required && <span className="required-badge">Requis</span>}
                            </div>
                            <label className="cookie-toggle">
                              <input
                                  type="checkbox"
                                  checked={preferences[category.key]}
                                  onChange={() => togglePreference(category.key)}
                                  disabled={category.required}
                              />
                              <span className="toggle-slider" />
                            </label>
                          </div>
                          <p>{category.description}</p>
                          <p className="cookie-example">
                            <strong>Exemples :</strong> {category.examples}
                          </p>
                        </div>
                    ))}
                  </div>

                  <div className="cookie-buttons">
                    <button
                        onClick={handleSavePreferences}
                        className="cookie-btn cookie-btn-accept"
                        disabled={isLoading}
                    >
                      {isLoading ? (
                          <>
                            <span className="loading-spinner" />
                            Sauvegarde...
                          </>
                      ) : (
                          'Enregistrer mes choix'
                      )}
                    </button>
                    <button
                        onClick={() => setShowSettings(false)}
                        className="cookie-btn cookie-btn-refuse"
                        disabled={isLoading}
                    >
                      Retour
                    </button>
                  </div>
                </>
            )}
          </div>
        </div>
      </div>
  );
};

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookiePreferences | null>(null);

  useEffect(() => {
    const consentData = localStorage.getItem('cookieConsent');
    if (consentData) {
      try {
        const parsed = JSON.parse(consentData);
        setConsent(parsed.preferences);
      } catch (error) {
        console.error('Erreur lecture cookies:', error);
      }
    }
  }, []);

  return {
    consent,
    hasConsent: (category: keyof CookiePreferences): boolean =>
        consent ? consent[category] : false
  };
};

export default CookieConsent;
